/* eslint-disable react/forbid-prop-types */
import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { confirmAlert } from 'react-confirm-alert';
import { useTranslation } from 'react-i18next';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './CannedMessageListItem.module.css';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const CannedMessageListItem = observer(function CannedMessageListItem(props) {
  const { message } = props;

  const { cannedMessageStore, sendingEvents, commonStore } = useRootStore();
  const [newItem, setNewItem] = React.useState(message);
  const [editItemActive, setEditItemActive] = React.useState(false);
  const [validationMessage, setValidationMessage] = React.useState('');
  const [newCursorPosition, setNewCursorPosition] = React.useState();

  const currentInputRef = React.useRef(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (editItemActive && currentInputRef && currentInputRef.current) {
      if (newCursorPosition) {
        currentInputRef.current.selectionStart = newCursorPosition;
        currentInputRef.current.selectionEnd = newCursorPosition;
      }
      currentInputRef.current.focus();
    }
  }, [editItemActive, newCursorPosition]);

  const toggleEditItem = () => {
    setEditItemActive(!editItemActive);
    setNewItem(message);
    setValidationMessage('');
  };

  const emitDeleteItemEvent = () => {
    sendingEvents.socketEmitDeleteCannedMessage(newItem);
    cannedMessageStore.actionDeleteMessage(newItem);
  };

  const confirmDeleteItem = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('Common.AreYouSure')}
          message={t('CannedMessageListItem.ConfirmDeleteMessage')}
          trueText={t('Common.Confirm')}
          trueEvent={emitDeleteItemEvent}
          falseText={t('Common.Close')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const onItemChange = (e) => {
    setNewItem({ ...message, Text: e.target.value });
  };

  const handleSubmitCannedMessage = debounce(() => {
    if (!newItem.Text || !newItem.Text.trim()) {
      setValidationMessage(t('CannedMessageListItem.MessageTextCantBeEmpty'));
      return;
    }

    const cannedMessage = {
      ...newItem,
      Text: newItem.Text,
    };

    sendingEvents.socketEmitUpsertCannedMessage(cannedMessage);
    setValidationMessage('');
    toggleEditItem();
  }, 250);

  const handleKeyDown = (event) => {
    const keyCode = event.keyCode || event.which;
    if (keyCode === 13) {
      // 13 is the enter keycode

      handleSubmitCannedMessage();
    }
  };

  const appendKeywordToInput = (e) => {
    if (currentInputRef?.current) {
      const cursorPosition = currentInputRef.current.selectionStart;
      const textBeforeCursorPosition = currentInputRef.current.value.substring(0, cursorPosition);
      const textAfterCursorPosition = currentInputRef.current.value.substring(
        cursorPosition,
        currentInputRef.current.value.length
      );
      const cannedMessageKeyword = e.target.value;
      const allText = textBeforeCursorPosition + cannedMessageKeyword + textAfterCursorPosition;

      setNewItem({ ...message, Text: allText });
      setNewCursorPosition(cursorPosition + cannedMessageKeyword.length);
    }
  };

  return (
    <React.Fragment>
      <Grid component="div" item xs={12} className={styles.cannedMessageListItem}>
        <Grid
          component="div"
          container
          className={
            editItemActive
              ? [styles.itemHolder, styles.itemHolderEditActive].join(' ')
              : styles.itemHolder
          }
        >
          {editItemActive ? (
            <Grid component="div" container item xs={11} align="left">
              <Grid component="div" item xs={12} className={styles.editInputContainer}>
                <input
                  id={newItem._id}
                  type="search"
                  value={newItem.Text || ''}
                  name="message"
                  placeholder={t('CannedMessageListItem.CannedMessage')}
                  className={styles.editItemInput}
                  onChange={onItemChange}
                  onKeyDown={handleKeyDown}
                  ref={currentInputRef}
                  data-testid="cannedMessageTextInput"
                  maxLength="1600"
                />

                {validationMessage ? (
                  <Grid component="div" item xs={12} hidden={!validationMessage}>
                    <span className={styles.validationText} data-testid="validationMessage">
                      {validationMessage}
                    </span>
                  </Grid>
                ) : null}

                <Grid component="div" item xs={12} className={styles.actionBtnsContainer}>
                  {commonStore?.cannedMessagesKeywords ? (
                    <div className={styles.cannedMessagesKeywordsContainer}>
                      {Object.entries(commonStore.cannedMessagesKeywords).map((entry) => {
                        const [keyword, value] = entry;
                        return (
                          <button
                            key={keyword}
                            type="button"
                            value={keyword}
                            className={styles.cannedMessagesKeywordsBtn}
                            onClick={appendKeywordToInput}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleSubmitCannedMessage}
                    className={[styles.btn, styles.submitBtn].join(' ')}
                    data-testid="saveBtn"
                  >
                    {t('Common.Save')}
                  </button>

                  <button
                    type="button"
                    onClick={toggleEditItem}
                    className={[styles.btn, styles.deleteBtn].join(' ')}
                  >
                    {t('Common.Cancel')}
                  </button>
                </Grid>
              </Grid>
            </Grid>
          ) : null}

          <Grid component="div" item xs={11} className={styles.btnsContainer}>
            <button
              className={[styles.cannedMessageBtn, styles.cannedMessageBlueBtn].join(' ')}
              type="button"
            >
              {message.Text}
            </button>
          </Grid>

          {!editItemActive ? (
            <Grid
              component="div"
              item
              xs={1}
              className={styles.optionsBtnsContainer}
              data-testid="moreOptions"
            >
              <button
                type="button"
                className={[styles.optionsBtn, styles.editIconBtn].join(' ')}
                onClick={toggleEditItem}
                data-testid="editIconBtn"
              />
              <button
                type="button"
                className={[styles.optionsBtn, styles.deleteIconBtn].join(' ')}
                onClick={confirmDeleteItem}
                data-testid="deleteIconBtn"
              />
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </React.Fragment>
  );
});
CannedMessageListItem.propTypes = {
  message: PropTypes.object,
};
CannedMessageListItem.whyDidYouRender = true;
CannedMessageListItem.displayName = 'CannedMessageListItem';

export default CannedMessageListItem;
